import React, { useState, useMemo, useRef, useEffect } from 'react';
import type { Icon as Interface } from '../../design/components';
import { iconMap, iconNames } from './iconMap';

export const Icon: React.FC<Interface> = ({
  name,
  description,
  state,
  placeholder = 'Select an icon',
  hidden,
  inline,
  readonly,
  disabled,
  required,
  error,
}) => {
  const [value, setValue] = state || ['', () => {}];
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);

  const filteredIcons = useMemo(() => {
    if (!search) return iconNames;
    const lower = search.toLowerCase();
    return iconNames.filter(name => name.toLowerCase().includes(lower));
  }, [search]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Inline mode: render just the icon
  if (inline) {
    const icon = value ? iconMap[value] : null;
    return icon ? (
      <span style={{ fontSize: '20px', display: hidden ? 'none' : undefined }}>{icon}</span>
    ) : null;
  }

  const handleClick = () => {
    if (!readonly && !disabled) {
      setIsOpen(true);
    }
  };

  const handleSelect = (iconName: string) => {
    setValue(iconName);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    setValue('');
  };

  const selectedIcon = value ? iconMap[value] : null;
  const fieldWidth = fieldRef.current?.offsetWidth || 360;

  return (
    <div style={{ marginBottom: '1rem', position: 'relative', display: hidden ? 'none' : undefined }} ref={containerRef}>
      <label htmlFor={name} style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
        {name}
        {required && <span style={{ color: 'red' }}> *</span>}
      </label>

      <div
        ref={fieldRef}
        onClick={handleClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          padding: '8px 12px',
          fontSize: '16px',
          border: error ? '1px solid red' : '1px solid #ccc',
          borderRadius: '4px',
          boxSizing: 'border-box',
          backgroundColor: disabled ? '#f5f5f5' : '#fff',
          cursor: readonly || disabled ? 'default' : 'pointer',
          gap: '8px',
        }}
      >
        <span style={{ fontSize: '20px', width: '24px', textAlign: 'center' }}>
          {selectedIcon || <span style={{ color: '#999' }}>?</span>}
        </span>
        <span style={{ color: value ? 'inherit' : '#999', flex: 1 }}>
          {value || placeholder}
        </span>
        {value && !readonly && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: '2px 6px',
              fontSize: '14px',
              color: '#666',
            }}
            aria-label="Clear icon"
          >
            ×
          </button>
        )}
      </div>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            width: fieldWidth,
            maxHeight: '400px',
            backgroundColor: '#fff',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
            marginTop: '4px',
          }}
        >
          <div style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
            <input
              type="text"
              placeholder="Search icons..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '14px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div
            style={{
              padding: '8px',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, 48px)',
              gap: '4px',
              maxHeight: '300px',
              overflowY: 'auto',
            }}
          >
            {filteredIcons.length === 0 ? (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '16px', color: '#666' }}>
                No icons found
              </div>
            ) : (
              filteredIcons.map((iconName) => {
                const isSelected = value === iconName;
                return (
                  <button
                    key={iconName}
                    type="button"
                    onClick={() => handleSelect(iconName)}
                    title={iconName}
                    style={{
                      padding: '8px',
                      border: 'none',
                      borderRadius: '4px',
                      backgroundColor: isSelected ? '#1976d2' : 'transparent',
                      color: isSelected ? '#fff' : '#333',
                      cursor: 'pointer',
                      fontSize: '20px',
                      textAlign: 'center',
                    }}
                  >
                    {iconMap[iconName]}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}

      {(error || description) && (
        <small style={{ color: error ? 'red' : '#666', display: 'block', marginTop: '4px' }}>
          {error || description}
        </small>
      )}
    </div>
  );
};
